import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import {
  fetchSearchCatalogFromNetwork,
  fetchSearchCatalogSnapshotRaw,
} from '../../services/g2TravelApi';
import './SearchCatalogResults.css';

const PAGE_SIZE = 150;

const normalize = (v) => (v || '').toString().toLowerCase();

const cityLabel = (c) =>
  (c.name || c.cityName || c.city || '').toString().trim() || '—';

const hotelLabel = (h) =>
  (h.name || h.propertyName || h.hotelName || '').toString().trim() || '—';

/** Plain name only (no country, id, etc.) for clipboard export. */
const cityNameOnly = (c) => (c.name || c.cityName || c.city || '').toString().trim();
const hotelNameOnly = (h) =>
  (h.name || h.propertyName || h.hotelName || '').toString().trim();

const SearchCatalogResults = ({ onGoHome, onNavigate }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cities, setCities] = useState([]);
  const [hotels, setHotels] = useState([]);
  const [snapshotMeta, setSnapshotMeta] = useState(null);
  const [dataSource, setDataSource] = useState('');
  const [filter, setFilter] = useState('');
  const [cityPage, setCityPage] = useState(0);
  const [hotelPage, setHotelPage] = useState(0);
  const [activeTab, setActiveTab] = useState('cities');
  const [copyFeedback, setCopyFeedback] = useState('');

  const handleCopyAllNames = async () => {
    const cityNames = cities.map(cityNameOnly).filter(Boolean);
    const hotelNames = hotels.map(hotelNameOnly).filter(Boolean);
    const text = [...cityNames, ...hotelNames].join('\n');
    if (!text) {
      setCopyFeedback('Nothing to copy');
      window.setTimeout(() => setCopyFeedback(''), 2500);
      return;
    }
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.setAttribute('readonly', '');
        ta.style.position = 'fixed';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      setCopyFeedback(`Copied ${cityNames.length + hotelNames.length} names`);
      window.setTimeout(() => setCopyFeedback(''), 2500);
    } catch {
      setCopyFeedback('Copy failed — try HTTPS or allow clipboard');
      window.setTimeout(() => setCopyFeedback(''), 3500);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const snap = await fetchSearchCatalogSnapshotRaw();
        if (cancelled) return;
        setSnapshotMeta(snap);
        const snapCities = Array.isArray(snap.cities) ? snap.cities : [];
        const snapHotels = Array.isArray(snap.hotels) ? snap.hotels : [];
        const snapEmpty = snap.empty === true || (snapCities.length === 0 && snapHotels.length === 0);

        if (!snapEmpty) {
          setCities(snapCities);
          setHotels(snapHotels);
          setDataSource('supabase_snapshot');
        } else {
          const net = await fetchSearchCatalogFromNetwork();
          if (cancelled) return;
          setCities(net.cities || []);
          setHotels(net.hotels || []);
          setDataSource('live_catalog_fallback');
        }
      } catch (e) {
        if (!cancelled) {
          setError(e?.message || 'Failed to load catalog');
          setCities([]);
          setHotels([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    setCityPage(0);
    setHotelPage(0);
  }, [filter]);

  const filteredCities = useMemo(() => {
    const q = normalize(filter).trim();
    if (!q) return cities;
    return cities.filter((c) => {
      const blob = [
        cityLabel(c),
        c.code,
        c.countryName,
        c.countryCode,
        c.id,
        c.cityId,
      ]
        .filter(Boolean)
        .map(normalize)
        .join(' ');
      return blob.includes(q);
    });
  }, [cities, filter]);

  const filteredHotels = useMemo(() => {
    const q = normalize(filter).trim();
    if (!q) return hotels;
    return hotels.filter((h) => {
      const blob = [
        hotelLabel(h),
        h.city,
        h.cityName,
        h.countryName,
        h.countryCode,
        h.id,
        h.code,
        h.cityId,
      ]
        .filter(Boolean)
        .map(normalize)
        .join(' ');
      return blob.includes(q);
    });
  }, [hotels, filter]);

  const citySlice = useMemo(() => {
    const start = cityPage * PAGE_SIZE;
    return filteredCities.slice(start, start + PAGE_SIZE);
  }, [filteredCities, cityPage]);

  const hotelSlice = useMemo(() => {
    const start = hotelPage * PAGE_SIZE;
    return filteredHotels.slice(start, start + PAGE_SIZE);
  }, [filteredHotels, hotelPage]);

  const cityPages = Math.max(1, Math.ceil(filteredCities.length / PAGE_SIZE));
  const hotelPages = Math.max(1, Math.ceil(filteredHotels.length / PAGE_SIZE));

  const fetchedAtText = snapshotMeta?.fetchedAt
    ? new Date(snapshotMeta.fetchedAt).toLocaleString()
    : '—';

  return (
    <div className="catalog-results-page">
      <Header onLogoClick={onGoHome} onNavigate={onNavigate} />
      <main className="catalog-results-main">
        <div className="catalog-results-container">
          <nav className="catalog-results-breadcrumb" aria-label="Breadcrumb">
            <Link to="/">Home</Link>
            <span aria-hidden="true"> / </span>
            <span>Search catalog</span>
          </nav>

          <header className="catalog-results-header">
            <h1 className="catalog-results-title">Search catalog results</h1>
            <p className="catalog-results-subtitle">
              Same city and hotel lists used by the home search bar: loaded from{' '}
              <strong>GET /api/search-catalog</strong> (Supabase table{' '}
              <code>search_catalog_snapshot</code>). If the snapshot is empty, the app falls back to
              live G2 endpoints — that is indicated below.
            </p>
          </header>

          {loading && (
            <div className="catalog-results-panel catalog-results-panel--loading">Loading catalog…</div>
          )}

          {error && !loading && (
            <div className="catalog-results-panel catalog-results-panel--error" role="alert">
              {error}
            </div>
          )}

          {!loading && !error && (
            <>
              <section className="catalog-results-meta" aria-label="Catalog summary">
                <div className="catalog-results-meta-grid">
                  <div className="catalog-results-stat">
                    <span className="catalog-results-stat-label">Cities</span>
                    <span className="catalog-results-stat-value">{cities.length}</span>
                  </div>
                  <div className="catalog-results-stat">
                    <span className="catalog-results-stat-label">Hotels / properties</span>
                    <span className="catalog-results-stat-value">{hotels.length}</span>
                  </div>
                  <div className="catalog-results-stat">
                    <span className="catalog-results-stat-label">Snapshot updated</span>
                    <span className="catalog-results-stat-value catalog-results-stat-value--small">
                      {fetchedAtText}
                    </span>
                  </div>
                  <div className="catalog-results-stat">
                    <span className="catalog-results-stat-label">Data source</span>
                    <span className="catalog-results-stat-value catalog-results-stat-value--small">
                      {dataSource === 'supabase_snapshot'
                        ? 'Supabase snapshot'
                        : dataSource === 'live_catalog_fallback'
                          ? 'Live G2 (snapshot was empty)'
                          : dataSource || '—'}
                    </span>
                  </div>
                </div>
              </section>

              <div className="catalog-results-toolbar">
                <label className="catalog-results-filter-label" htmlFor="catalog-filter">
                  Filter
                </label>
                <input
                  id="catalog-filter"
                  type="search"
                  className="catalog-results-filter-input"
                  placeholder="Filter by name, city, country, code…"
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  autoComplete="off"
                />
                <button
                  type="button"
                  className="catalog-results-copy-btn"
                  onClick={handleCopyAllNames}
                  disabled={cities.length === 0 && hotels.length === 0}
                >
                  Copy all names
                </button>
                {copyFeedback ? (
                  <span className="catalog-results-copy-feedback" role="status">
                    {copyFeedback}
                  </span>
                ) : null}
              </div>

              <div className="catalog-results-tabs" role="tablist">
                <button
                  type="button"
                  role="tab"
                  aria-selected={activeTab === 'cities'}
                  className={`catalog-results-tab ${activeTab === 'cities' ? 'is-active' : ''}`}
                  onClick={() => setActiveTab('cities')}
                >
                  Cities ({filteredCities.length})
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={activeTab === 'hotels'}
                  className={`catalog-results-tab ${activeTab === 'hotels' ? 'is-active' : ''}`}
                  onClick={() => setActiveTab('hotels')}
                >
                  Hotels ({filteredHotels.length})
                </button>
              </div>

              {activeTab === 'cities' && (
                <section className="catalog-results-table-wrap" aria-label="Cities list">
                  <table className="catalog-results-table">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Code</th>
                        <th>Country</th>
                        <th>Id</th>
                      </tr>
                    </thead>
                    <tbody>
                      {citySlice.map((c, i) => (
                        <tr key={`${c.id || c.code || cityLabel(c)}-${i}`}>
                          <td>{cityLabel(c)}</td>
                          <td>{(c.code || '').toString()}</td>
                          <td>{(c.countryName || c.countryCode || '').toString()}</td>
                          <td className="catalog-results-mono">{(c.id || c.cityId || '').toString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {filteredCities.length === 0 && (
                    <p className="catalog-results-empty">No cities in this catalog.</p>
                  )}
                  {filteredCities.length > PAGE_SIZE && (
                    <div className="catalog-results-pagination">
                      <button
                        type="button"
                        className="catalog-results-page-btn"
                        disabled={cityPage <= 0}
                        onClick={() => setCityPage((p) => Math.max(0, p - 1))}
                      >
                        Previous
                      </button>
                      <span className="catalog-results-page-info">
                        Page {cityPage + 1} of {cityPages} (showing {PAGE_SIZE} per page)
                      </span>
                      <button
                        type="button"
                        className="catalog-results-page-btn"
                        disabled={cityPage >= cityPages - 1}
                        onClick={() => setCityPage((p) => Math.min(cityPages - 1, p + 1))}
                      >
                        Next
                      </button>
                    </div>
                  )}
                </section>
              )}

              {activeTab === 'hotels' && (
                <section className="catalog-results-table-wrap" aria-label="Hotels list">
                  <table className="catalog-results-table">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>City</th>
                        <th>Country</th>
                        <th>Id / code</th>
                      </tr>
                    </thead>
                    <tbody>
                      {hotelSlice.map((h, i) => (
                        <tr key={`${h.id || h.code || hotelLabel(h)}-${i}`}>
                          <td>{hotelLabel(h)}</td>
                          <td>{(h.city || h.cityName || '').toString()}</td>
                          <td>{(h.countryName || h.countryCode || '').toString()}</td>
                          <td className="catalog-results-mono">
                            {(h.id || h.propertyId || h.code || '').toString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {filteredHotels.length === 0 && (
                    <p className="catalog-results-empty">No hotels in this catalog.</p>
                  )}
                  {filteredHotels.length > PAGE_SIZE && (
                    <div className="catalog-results-pagination">
                      <button
                        type="button"
                        className="catalog-results-page-btn"
                        disabled={hotelPage <= 0}
                        onClick={() => setHotelPage((p) => Math.max(0, p - 1))}
                      >
                        Previous
                      </button>
                      <span className="catalog-results-page-info">
                        Page {hotelPage + 1} of {hotelPages} (showing {PAGE_SIZE} per page)
                      </span>
                      <button
                        type="button"
                        className="catalog-results-page-btn"
                        disabled={hotelPage >= hotelPages - 1}
                        onClick={() => setHotelPage((p) => Math.min(hotelPages - 1, p + 1))}
                      >
                        Next
                      </button>
                    </div>
                  )}
                </section>
              )}
            </>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default SearchCatalogResults;
